import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useFormsTranslation } from '@/hooks/useTranslation';
import { Info, Plus } from 'lucide-react';
import {
  Control,
  FieldArrayWithId,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
} from 'react-hook-form';
import { FormFieldWrapper } from '../../../FormFieldWrapper';
import { IdentificationFormData, PoliticallyExposedPerson } from '../types';
import { PepCard } from './PepCard';

interface PepSectionProps {
  control: Control<IdentificationFormData>;
  showPepFields: boolean;
  pepFields: FieldArrayWithId<
    IdentificationFormData,
    'politicallyExposedPersons',
    'id'
  >[];
  appendPep: UseFieldArrayAppend<
    IdentificationFormData,
    'politicallyExposedPersons'
  >;
  removePep: UseFieldArrayRemove;
  defaultPepValues: PoliticallyExposedPerson;
}

export function PepSection({
  control,
  showPepFields,
  pepFields,
  appendPep,
  removePep,
  defaultPepValues,
}: PepSectionProps) {
  const { t: tForms } = useFormsTranslation();

  return (
    <div className='space-y-6'>
      <div className='flex items-center gap-2 mb-4'>
        <h4 className='text-lg font-semibold'>
          {tForms('identification.sections.politicallyExposedPersons')}
        </h4>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type='button'
                variant='ghost'
                size='sm'
                className='p-1 h-6 w-6 text-muted-foreground hover:text-foreground'
                aria-label={tForms(
                  'identification.ariaLabels.informationAbout',
                  {
                    section: tForms(
                      'identification.sections.politicallyExposedPersons'
                    ),
                  }
                )}
              >
                <Info className='h-4 w-4' />
              </Button>
            </TooltipTrigger>
            <TooltipContent side='right' className='max-w-3xl'>
              <div className='space-y-3 text-sm'>
                <p className='font-semibold'>
                  {tForms('identification.descriptions.pepDefinitionTitle')}
                </p>
                <p>{tForms('identification.descriptions.pepDefinitionA')}</p>
                <p>{tForms('identification.descriptions.pepDefinitionB')}</p>
                <p>{tForms('identification.descriptions.pepDefinitionC')}</p>
                <p>{tForms('identification.descriptions.pepDefinitionD')}</p>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Question: Are PEP involved? */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
        <FormField
          control={control}
          name='isPepInvolved'
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('identification.fields.isPepInvolved')}
              name='isPepInvolved'
              error={fieldState.error?.message}
            >
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className='min-h-[44px] w-full'>
                  <SelectValue
                    placeholder={tForms(
                      'identification.placeholders.selectOption'
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='yes'>
                    {tForms('identification.labels.yes')}
                  </SelectItem>
                  <SelectItem value='no'>
                    {tForms('identification.labels.no')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </FormFieldWrapper>
          )}
        />
        {/* Empty space for alignment */}
        <div></div>
      </div>

      {/* PEP Details - shown when isPepInvolved === 'yes' */}
      {showPepFields && (
        <div className='space-y-6'>
          {pepFields.map((field, index) => (
            <PepCard
              key={field.id}
              control={control}
              index={index}
              onRemove={() => removePep(index)}
              canRemove={pepFields.length > 1}
            />
          ))}

          <Button
            type='button'
            variant='outline'
            onClick={() => appendPep(defaultPepValues)}
            className='w-full'
          >
            <Plus className='w-4 h-4 mr-2' />
            {tForms('identification.buttons.addPep')}
          </Button>
        </div>
      )}
    </div>
  );
}
